import {
	FormControl,
	FormControlLabel,
	FormHelperText,
	FormLabel,
	Radio,
	RadioGroup,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import Button from "@mui/material/Button";
import Grid, { type GridSize } from "@mui/material/Grid2";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useAppSelector } from "@store/hooks";
import {
	handleBasicChange,
	validateBookingPaymentTab,
	validateTab,
} from "@utils/general";
import { StringProcessor } from "@utils/stringProcessor";
import { validateBookingPaymentMethod } from "@utils/validation";
import { useSanitizer } from "@utils/validation/hooks";
import React from "react";
import useDebugInformation from "../../hooks/useDebugInformation";
import { api, ui /* state, */ } from "../../interfaces/interfaces"; // state unused
import CompaniesHouseRecordDialog from "./CompaniesHouseRecordDialog";

const name = "PaymentMethodTab";

type A11yProps = (
	name: MemoizedInputProps["inputName"],
	prefix: MemoizedDropdownInputProps["prefix"],
) => {
	id: string;
	name: string;
	fullWidth: boolean;
	autoComplete: string;
	"aria-controls": string;
	onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
};

export const a11yProps: A11yProps = (name, prefix) => {
	return {
		id: `input-${name.toLowerCase()}`,
		name,
		fullWidth: true,
		autoComplete: "none",
		"aria-controls": `action-input-${name.toLowerCase()}`,
		onChange: (event) => handleBasicChange(event, prefix),
	};
};

// const validateValue = (inputName: string, value: any) => {
//     inputName = inputName.replace("BookingPayment", "")
//     if (inputName === "PaymentRequisitions") {
//         return value?.id > -1
//     }
//     else if (inputName === "PaymentMethods") {
//         return value?.id > -1 && (value?.id === 3 ? ui.BookingPaymentCardAgreed : true)
//     }
//     else {
//         return value?.id > -1
//     }
// }

type MemoizedInputProps = {
	parent: string;
	prefix: "BookingPaymentMethod";
	inputName: "CompanyRegistration";
	xs: GridSize;
	sm: GridSize;
	error?: boolean;
};

type MemoizedDropdownInputProps = Omit<MemoizedInputProps, "inputName"> & {
	inputName: "PaymentMethods" | "PaymentRequisitions";
};

const MemoizedInput = React.memo<MemoizedInputProps>(
	({ parent, prefix, inputName, xs, sm }) => {
		const monitored = useAppSelector(({ ui }) => ui[`${prefix}${inputName}`]);

		// useSanitizer is used to sanitize the input value
		const sanitized = useSanitizer(monitored, inputName);

		// useValidator is used to validate the input value
		const helperText = useAppSelector(
			({ ui }) => ui[`${prefix}${inputName}HelperText`],
		);
		// useValidator(prefix, inputName, sanitized)

		const valid = useAppSelector(
			({ state }) => state[`${prefix}${inputName}Valid`],
		);

		useDebugInformation(`${parent}▷[${inputName}]`, {
			inputName,
			prefix,
			xs,
			sm,
			monitored,
		});
		return (
			<Grid
				size={{
					xs,
					sm,
				}}
			>
				{" "}
				<TextField
					value={sanitized}
					required
					{...a11yProps(inputName, prefix)}
					label={StringProcessor.camelToWords(inputName)}
					error={!valid}
					color={valid ? "primary" : "error"}
					helperText={!valid ? helperText : ""}
				/>
			</Grid>
		);
	},
);

const MemoizedDropdownInput = React.memo<MemoizedDropdownInputProps>(
	({ parent, prefix, inputName, xs, sm, error }) => {
		const value = useAppSelector(({ ui }) => ui[`${prefix}${inputName}`]);
		const options = useAppSelector(({ state }) => state[`${inputName}`]);
		// useSanitizer is used to sanitize the input value
		const helperText = useAppSelector(
			({ ui }) => ui[`${prefix}${inputName}HelperText`],
		);
		const valid = useAppSelector(
			({ state }) => state[`${prefix}${inputName}Valid`],
		);

		useDebugInformation(`${parent}▷[${inputName}]`, {
			inputName,
			parent,
			prefix,
			xs,
			sm,
			options,
		});

		const handleChange = (event: React.ChangeEvent<{}>, value: any) => {
			if (value) {
				ui[`${prefix}${inputName}`] = value;
			} else {
				ui[`${prefix}${inputName}`] = null;
			}
		};

		return (
			<Grid
				size={{
					xs,
					sm,
				}}
			>
				<Autocomplete
					id={`combo-box-${inputName}`}
					options={
						inputName === "PaymentMethods"
							? options.filter((o: { name: any }) =>
									["Credit Card", "Invoice", "Credit Card (On Site)"].includes(
										o?.name,
									),
								)
							: options
					}
					getOptionLabel={(option) => (option?.name ? option.name : "")}
					onChange={handleChange}
					isOptionEqualToValue={(option, value) => option?.id === value?.id}
					value={value}
					sx={{ mt: 1 }}
					renderInput={(params: any) => (
						<TextField
							required
							{...params}
							label={StringProcessor.camelToWords(inputName).replace(
								"Frequencies",
								"Frequency",
							)}
							name={StringProcessor.camelToWords(inputName)}
							error={!valid || error}
							color={valid ? "primary" : "error"}
							helperText={!valid ? helperText : ""}
						/>
					)}
				/>
			</Grid>
		);
	},
);

type Props = {
	prefix: "BookingPaymentMethod";
	parent: string;
};

const getCompaniesHouseRecord = async (setProfile: any) => {
	const data = await api.fetchCompanyProfile(
		ui.BookingPaymentMethodCompanyRegistration,
	);
	if (data && data.company_number) {
		setProfile(data);
		ui.CompaniesHouseRecordDialog = true;
	} else {
		ui.ProblemMsg = `No data found for registration number ${ui.BookingPaymentMethodCompanyRegistration}`;
		ui.ProblemMsgVariant = "error";
		ui.ProblemShowMsg = true;
	}
};

const handleAgreementChange = (event: React.ChangeEvent<HTMLInputElement>) => {
	// create arrow function to convert string to boolean
	ui.BookingPaymentMethodCardAgreed =
		"true" === (event.target as HTMLInputElement).value;
};

export const PaymentMethodTab: React.FC<Props> = ({ prefix, parent }) => {
	const companyRegistration = useAppSelector(
		({ ui }) => ui.BookingPaymentMethodCompanyRegistration,
	);
	const isAccepted = useAppSelector(
		({ state }) => state.BookingPaymentMethodCompanyRegistrationAccepted,
	);
	const isCompanyRegistrationAccepted = useAppSelector(
		({ state }) => state.BookingPaymentMethodCompanyRegistrationAccepted,
	);
	const isCustomerAgreed = useAppSelector(
		({ ui }) => ui.BookingPaymentMethodCardAgreed,
	);
	const isRequisition = useAppSelector(
		({ state }) => state.BookingPaymentMethodIsRequisition,
	);
	const isRequisitionValid = useAppSelector(
		({ state }) => state.BookingPaymentMethodPaymentRequisitionsValid,
	);
	const paymentMethod = useAppSelector(
		({ ui }) => ui.BookingPaymentMethodPaymentMethods,
	);
	const paymentRequisition = useAppSelector(
		({ ui }) => ui.BookingPaymentMethodPaymentRequisitions,
	);
	const [companyProfile, setCompanyProfile] = React.useState<any | undefined>();

	React.useEffect(() => {
		validateBookingPaymentTab("BookingPayment");
	}, [
		paymentMethod,
		isCustomerAgreed,
		companyRegistration,
		isCompanyRegistrationAccepted,
		isRequisitionValid,
	]);

	React.useEffect(() => {
		validateBookingPaymentMethod(
			"BookingPaymentMethod",
			"PaymentRequisitions",
			paymentRequisition?.name ? paymentRequisition.name : "",
		);
		validateTab("BookingPayment");
	}, [paymentRequisition]);

	const debugName = `${parent}▷[${name}]`;
	useDebugInformation(debugName, { prefix, parent, paymentMethod });
	return (
		<>
			<CompaniesHouseRecordDialog companyProfile={companyProfile} />
			<Grid container spacing={2} style={{ overflow: "hidden", width: "100%" }}>
				<MemoizedDropdownInput
					inputName="PaymentMethods"
					parent={debugName}
					prefix={prefix}
					xs={12}
					sm={12}
					error={!isCustomerAgreed && paymentMethod?.id === 3}
				/>
				{isRequisition && (
					<MemoizedDropdownInput
						inputName="PaymentRequisitions"
						parent={debugName}
						prefix={prefix}
						xs={12}
						sm={12}
					/>
				)}
				{paymentMethod?.id === 3 && (
					<Grid
						size={{
							xs: 12,
							sm: 12,
						}}
					>
						<FormControl error={!isCustomerAgreed}>
							<FormLabel id="demo-controlled-radio-buttons-group">
								Customer agrees to store card details to secure booking?
							</FormLabel>
							<FormHelperText>
								{isCustomerAgreed ? "" : "Select different payment method"}
							</FormHelperText>
							<RadioGroup
								aria-labelledby="demo-controlled-radio-buttons-group"
								name="controlled-radio-buttons-group"
								value={isCustomerAgreed}
								onChange={handleAgreementChange}
							>
								<FormControlLabel
									value="true"
									control={<Radio />}
									label="Yes"
								/>
								<FormControlLabel
									value="false"
									control={<Radio />}
									label="No"
								/>
							</RadioGroup>
						</FormControl>
					</Grid>
				)}

				{isRequisition && isRequisitionValid && (
					<Grid
						size={{
							xs: 12,
							sm: 12,
						}}
					>
						<Stack direction={"row"} gap={2} sx={{ height: "56px" }}>
							<MemoizedInput
								inputName="CompanyRegistration"
								parent={debugName}
								prefix={prefix}
								xs={12}
								sm={12}
							/>
							{companyRegistration && (
								<Button
									type="button"
									variant="contained"
									sx={{ maxHeight: "3.3rem" }}
									onClick={() => getCompaniesHouseRecord(setCompanyProfile)}
								>
									Validate
								</Button>
							)}
						</Stack>
					</Grid>
				)}
				<Grid
					sx={{ height: 0, position: "relative", left: "14px", top: "-21px" }}
					size={{
						xs: 12,
						sm: 12,
					}}
				>
					{companyProfile &&
						String(companyRegistration).length === 8 &&
						!isAccepted && (
							<Typography sx={{ fontSize: ".75rem" }} color="#d32f2f">
								Please validate or change payment method if rejected.
							</Typography>
						)}
				</Grid>
			</Grid>
			<Typography>{isRequisition}</Typography>
		</>
	);
};

export default React.memo(PaymentMethodTab);
